<script setup>
import {tvCompactTeamName, tvGridCellScale, tvGridSize} from '~/utils/tvDisplay';

// A league division's results grid: every player down the side in table order,
// numbered, with the same numbers across the top; each cell is the row player's
// score against the column player. Wins are lit; cells shrink as divisions grow.
const props = defineProps({
  panel: {type: Object, required: true},
});

const grid = computed(() => props.panel.grid ?? {teams: [], cells: []});
const mode = inject('tvMode', ref('wide'));
const size = computed(() => tvGridSize(grid.value.teams.length, mode.value));
const hasBonus = computed(() => grid.value.teams.some(t => t.bonusPoints));
const cellOf = computed(() => {
  const map = new Map();
  for (const cell of grid.value.cells) map.set(`${cell.rowTeamId}:${cell.columnTeamId}`, cell);
  return (row, column) => map.get(`${row.id}:${column.id}`);
});
</script>

<template>
  <table class="tv-grid" :style="{'--grid-row': size.row + 'px', '--grid-font': size.font + 'px'}">
    <thead>
      <tr>
        <th class="tv-grid-corner"/>
        <th v-for="(column, c) in grid.teams" :key="column.id" class="tv-grid-head">{{ c + 1 }}</th>
        <th class="tv-grid-head tv-grid-stat">P</th>
        <th v-if="hasBonus" class="tv-grid-head tv-grid-stat">B</th>
        <th class="tv-grid-head tv-grid-stat">Pts</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="(row, r) in grid.teams" :key="row.id">
        <th class="tv-grid-name">
          <span class="tv-grid-pos">{{ r + 1 }}</span>
          <span class="tv-grid-team">{{ tvCompactTeamName(row.name) }}</span>
        </th>
        <td v-for="column in grid.teams" :key="column.id" class="tv-grid-cell"
            :class="row.id === column.id ? 'tv-grid-cell--self' : `tv-grid-cell--${(cellOf(row, column)?.outcome || 'none').toLowerCase()}`"
            :style="row.id === column.id ? null : {fontSize: tvGridCellScale(cellOf(row, column)?.score) + 'em'}">
          {{ row.id === column.id ? '' : cellOf(row, column)?.score }}
        </td>
        <td class="tv-grid-stat">{{ row.played }}</td>
        <td v-if="hasBonus" class="tv-grid-stat">{{ row.bonusPoints }}</td>
        <td class="tv-grid-stat tv-grid-stat--pts">{{ row.totalPoints }}</td>
      </tr>
    </tbody>
  </table>
</template>

<style scoped lang="scss">
.tv-grid {
  width: 100%;
  border-collapse: separate;
  border-spacing: 3px;
  table-layout: fixed;
  font-size: var(--grid-font);
}

.tv-grid-corner {
  width: 280px;
}

.tv-grid-head {
  /* Result columns share whatever width the names and totals leave */
  width: auto;
  padding-bottom: 4px;
  font-family: var(--tv-display);
  font-weight: 600;
  color: var(--tv-muted);
  text-align: center;
}

.tv-grid-name {
  max-width: 280px;
  height: var(--grid-row);
  padding-right: 10px;
  text-align: left;
  font-weight: 400;
  color: var(--tv-ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: -0.3px;
}

.tv-grid-pos {
  display: inline-block;
  width: 1.6em;
  font-family: var(--tv-display);
  font-weight: 700;
  color: var(--tv-muted);
}

.tv-grid-cell {
  height: var(--grid-row);
  border-radius: 4px;
  background: var(--tv-chip);
  text-align: center;
  font-family: var(--tv-display);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--tv-muted);
  white-space: nowrap;
  overflow: hidden;
  /* Very long wording, even scaled down, ends with an ellipsis rather than a clipped letter */
  text-overflow: ellipsis;
}

/* The row player's own column */
.tv-grid-cell--self {
  background: repeating-linear-gradient(135deg, var(--tv-chip), var(--tv-chip) 4px, transparent 4px, transparent 8px);
}

.tv-grid-cell--w {
  background: var(--tv-accent-tint);
  color: var(--tv-ink);
  font-weight: 800;
}

.tv-grid-cell--d {
  color: var(--tv-ink);
}

/* The table's figures at the end of each row, set apart from the grid */
.tv-grid-head.tv-grid-stat,
.tv-grid-stat {
  width: 64px;
  text-align: right;
  font-family: var(--tv-display);
  font-variant-numeric: tabular-nums;
  color: var(--tv-ink);
  padding-left: 8px;
}

td.tv-grid-stat {
  font-size: calc(var(--grid-font) * 1.1);
  font-weight: 600;
}

.tv-grid-head.tv-grid-stat {
  padding-left: 18px;
}

.tv-grid-stat--pts {
  font-weight: 800 !important;
}
</style>
